import { render, Component } from 'preact'
import { createPortal } from 'preact/compat'
import Comment from './Comments/Comment'
import CommentsWrapper from './Comments/Wrapper'
import ReviewForm from './Form'
import Ratings from './Ratings/Base'
import Modal from './Modal'
import Stars from './Stars'
import { auth } from './helpers'

const REVIEWS_PER_PAGE = 3

export {
  Comment,
  CommentsWrapper,
  ReviewForm,
  Ratings,
  Modal,
  Stars,
  REVIEWS_PER_PAGE,
}

/**
 * Given a set of ratings, average in a new arting
 * @param {number} newest  Incoming rating
 * @param {number} old     Previous agg. rating
 * @param {number} count   New rating count
 */
const avgRatings = (newest, old, count) => ((old * (count - 1)) + newest) / count

/**
 * Given a set of elements with "mv-create-rating" attribute, generate an initial array
 * of card data.
 */
const getCards = () => Array.from(document.querySelectorAll('[data-mv-create-rating]'))
  .map((el) => {
    const id = el.getAttribute('data-mv-create-id')
    const rating = Number(el.getAttribute('data-mv-create-rating'))
    const total = el.getAttribute('data-mv-create-total-ratings')

    return ({
      id,
      el,
      rating,
      total,
      reviews: [],
      // FIXME: Is there a better way of deriving the title from the div?
      title: el.closest('.mv-create-card').querySelector('.mv-create-title').textContent,
    })
  })

  /**
 * Given an element, insert a div beforehand and apply box model of original element to new element.
 * @param  {DOMNode} el Element on which to mount
 * @return {array}      Duple with index 0 being the created element
 *                      and index 1 being the original element
 */
const makeCommentsBase = (el) => {
  const parent = el.parentNode
  const wrap = document.createElement('DIV')

  // llc comments adds a wrapper div which doesn't have the styles that we need so we'll grab it from a different element
  const elToGetStylesFrom = el.id === 'llc_comments' ? document.querySelector('.entry-content') : el
  const styleRef = getComputedStyle(elToGetStylesFrom);

  wrap.style['width'] = styleRef['width']
  wrap.className = 'mv-reviews-cta-wrap'
  
  parent.insertBefore(wrap, el)
  return [wrap, el]
}

/**
 * Manages central data store for revivew data.
 * Uses the localized setting for the review prompt and autosubmit threshold.
 */
class App extends Component {
  constructor(props) {
    super(props)

    // Get original cards and reduce array into key-value pairs
    // for card ids and review totals/average ratings, respectively.
    // By keeping these as objects, we make lookups and updates simplier.
    this.cards = getCards()
    const totalsByCardId = this.cards.reduce((obj, { id, total }) => ({ ...obj, [id]: total }), {})
    const ratingByCardId = this.cards.reduce(
      (obj, { id, rating }) => ({ ...obj, [id]: rating }),
      {},
    )

    this.state = {
      totalsByCardId,
      ratingByCardId,
      reviewsByCardId: {}, // We need to fetch these async, so we initializes as an empty object.
    }

    this.getInitialReviews = this.getInitialReviews.bind(this)
    this.getCard = this.getCard.bind(this)
    this.addExistingReview = this.addExistingReview.bind(this)
    this.updateExistingReview = this.updateExistingReview.bind(this)
    this.addNewReview = this.addNewReview.bind(this)
  }

  componentDidMount() {
    this.getInitialReviews()
  }

  /**
   * Pre-loads three review for reach card
   */
  getInitialReviews() {
    const requests = this.cards.map(({ id }) => {
      // Get the first three reviews for each card by ID and map to a key-value pair to be flattened
      const resultAsId = auth.get(
        `mv-create/v1/reviews?creation=${id}&limit=${REVIEWS_PER_PAGE}`,
      )
      // Return a tuple of our data object and response headers
      .then(({ data, headers }) => ([
        id,
        data,
        headers.get('x-total-items'),
      ]))

      return resultAsId
    })

    Promise.all(requests)
      .then(reviews => {
        let reviewsByCardId = {}
        let totalsByCardId = {}
        let ratingByCardId = {}

        reviews.forEach(([id, reviews, total]) => {
          reviewsByCardId[id] = reviews
          totalsByCardId[id] = total
          ratingByCardId[id] = reviews.reduce((avg, { rating }) => avg + Number(rating), 0) / reviews.length
        })

        this.setState({ reviewsByCardId, totalsByCardId, ratingByCardId })
      })
  }

  /**
   * Maps card data into data accessed via dictionary
   * @param  {number} id   Card ID
   * @params {object} rest Rest of data to display by default
   */
  getCard({ id, ...rest }) {
    const { totalsByCardId, ratingByCardId, reviewsByCardId } = this.state
    return ({
      ...rest,
      id,
      total: totalsByCardId[id],
      reviews: reviewsByCardId[id] || [],
    })
  }

  /**
   * Attaches reviews to global state when loaded via pagination
   * @param  {...any} reviews
   */
  addExistingReview(...reviews) {
    const { creation } = reviews[0]
    this.setState(state => ({
      reviewsByCardId: {
        ...state.reviewsByCardId,
        [creation]: [...state.reviewsByCardId[creation], ...reviews],
      },
    }))
  }

  /**
   * When a user leaves a review, add it to the state and update average ratings/totals
   * @param {number} id       ID of card to attach review to
   * @param {object} review   Review contents
   */
  addNewReview(id, review) {
    const { creation, rating } = review

    // If review was edited, we want to update it instead
    if (review.edited_by_user == 1) { // eslint-disable-line
      return this.updateExistingReview(id, review)
    }

    const newTotal = Number(this.state.totalsByCardId[creation]) + 1

    return this.setState(state => ({
      totalsByCardId: {
        ...state.totalsByCardId,
        [creation]: newTotal,
      },
      ratingByCardId: {
        ...state.ratingByCardId,
        [creation]: avgRatings(
          Number(rating),
          Number(state.ratingByCardId[creation]),
          newTotal,
        ),
      },
      reviewsByCardId: {
        ...state.reviewsByCardId,
        [creation]: [review, ...state.reviewsByCardId[creation]],
      },
    }))
  }

  /**
   * When a user updates an existing review, update in state
   * @param {number} creation        ID of card
   * @param {object} updatedReview   Updated review contents
   */
  updateExistingReview(creation, updatedReview) {
    const reviewsByCardId = this.state.reviewsByCardId[creation]
    const updatedReviews = reviewsByCardId.map((review) => {
      if (review.id == updatedReview.id) { // eslint-disable-line
        return updatedReview
      }
      return review
    })

    const { rating } = updatedReview

    this.setState(state => ({
      ratingByCardId: {
        ...state.ratingByCardId,
        [creation]: avgRatings(
          parseFloat(rating),
          parseFloat(state.ratingByCardId[creation]),
          state.totalsByCardId[creation],
        ),
      },
      reviewsByCardId: {
        ...state.reviewsByCardId,
        [creation]: updatedReviews,
      },
    }))
  }

  render() {
    const { el } = this.props // eslint-disable-line
    const cards = this.cards.map(this.getCard)

    return (
      <div>
        {/* Map over each card and mount upon the ratings element. */}
        {cards
          .map(card => (
            createPortal(
              <Ratings
                {...card}
                onAdd={this.addNewReview}
                onUpdate={this.updateExistingReview}
              />, 
              card.el
            )

          ))}

        {/* If public reviews are enabled, mount the comments wrapper */}
        {el && (
          <CommentsWrapper
            el={el}
            items={cards}
            onAdd={this.addNewReview}
            onUpdate={this.updateExistingReview}
            onLoad={this.addExistingReview}
          />
        )}
      </div>
    )
  }
}

export default () => {
  const id = window.MV_CREATE_SETTINGS.__REVIEWS_DIV__ // eslint-disable-line

  const root = id && document.querySelector(id)
  // If this isn't defined, we can't run our application.
  // (This would only NOT be defined for content types that don't support reviews.)
  const apiRoot = document.querySelector('[data-mv-rest-url]')
  if (!apiRoot) return

  // If we have an id to mount on, we need to provide `el` as a prop to our app
  if (root) {
    const [base, comments] = makeCommentsBase(root)
    render(<App el={comments} />, base)
  } else {
    // This is a bit weird. If we don't have a root to mount the "reviews" app,
    // we still need we need a DOM node in order to mount the Preact application
    // that for each "review." The app works by rendering a portal into each
    // card while centralizing the application logic in one place, so that *when*
    // there is a root for the "reviews" app, it can know about the cards on the
    // page for things like updating when someone leaves a review, e.g.
    // Sooooooo that's what this div is used for – TLDR; we need any random div
    // to mount a single Preact app on and the Preact app doesn't actually render
    // anything in that div.
    // https://github.com/developit/preact-portal
    const base = document.createElement('div')
    if (!base || base.dataset.hasLoaded) {
      return
    }
    base.dataset.hasLoaded = true
    render(<App />, base)
  }

  window.MV_REVIEWS_INIT = true
}
